import { Input, Tree } from 'antd';
import { DataNode } from 'antd/es/tree';
import { Key, memo, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useAreaStore from 'src/stores/area/area.store';
import { FUNCS } from 'src/utils';

const { Search } = Input;

const getParentKey = (key: React.Key, tree: DataNode[]): React.Key => {
  let parentKey: React.Key;
  tree.forEach((node) => {
    if (node.children) {
      if (node.children.some((item) => item.key === key)) {
        parentKey = node.key;
      } else if (getParentKey(key, node.children)) {
        parentKey = getParentKey(key, node.children);
      }
    }
  });

  return parentKey!;
};

function SiderLayout() {
  const { area, changeAreaActive } = useAreaStore();
  const { pathname } = useLocation();

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  const data = useMemo<DataNode[]>(() => {
    if (area.list) {
      return area.list.map((i) => {
        return {
          title: i.name,
          key: i.id,
          children: i.communes.map((x) => ({
            title: x.name,
            key: `${i.id}.${x.id}`,
            children: x.villages.map((y) => ({
              title: y.name,
              key: `${i.id}.${x.id}.${y.id}`,
            })),
          })),
        };
      });
    }
    return [];
  }, [area.list]);

  const dataList = useMemo<{ key: React.Key; title: string }[]>(() => {
    const result: { key: React.Key; title: string }[] = [];
    const generateList = (data: DataNode[]) => {
      data.forEach((node) => {
        const { key, title } = node;
        result.push({ key, title: title as string });
        if (node.children) {
          generateList(node.children);
        }
      });
    };
    generateList(data);
    return result;
  }, [data]);

  const onExpand = (newExpandedKeys: React.Key[]) => {
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(false);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    const newExpandedKeys = dataList
      .map((item) => {
        const title = item.title as string;
        if (
          FUNCS.removeDiacritics(title).toLocaleLowerCase().indexOf(FUNCS.removeDiacritics(value).toLocaleLowerCase()) >
          -1
        ) {
          return getParentKey(item.key, data);
        }
        return null;
      })
      .filter((item, i, self): item is React.Key => !!(item && self.indexOf(item) === i));
    setExpandedKeys(newExpandedKeys);
    setSearchValue(value);
    setAutoExpandParent(true);
  };

  const filterData = useMemo<DataNode[]>(() => {
    const loop = (data: DataNode[]): DataNode[] =>
      data.map((item) => {
        const strTitle = item.title as string;
        const search = FUNCS.removeDiacritics(searchValue as string).toLocaleLowerCase();
        const index = FUNCS.removeDiacritics(strTitle).toLocaleLowerCase().indexOf(search);
        const beforeStr = strTitle.substring(0, index);
        const searchTitle = strTitle.substring(index, index + search.length);
        const afterStr = strTitle.slice(index + search.length);
        const title =
          index > -1 ? (
            <span>
              {beforeStr}
              <span style={{ color: 'blue' }}>{searchTitle}</span>
              {afterStr}
            </span>
          ) : (
            <span>{strTitle}</span>
          );
        if (item.children) {
          return { title, name: strTitle, key: item.key, children: loop(item.children) };
        }

        return {
          title,
          key: item.key,
          name: strTitle,
        };
      });

    return [{ title: pathname === '/noi-dung' ? 'Cá nhân' : 'Tất cả', key: '', name: 'Cá nhân' }, ...loop(data)];
  }, [data, searchValue, pathname]);

  const onChooseArea = (ids: Key[], info: any) => {
    const [id] = ids;
    changeAreaActive({ active: id as string, activeName: info.selected ? info.node.name : '' });
  };
  const { innerHeight: height } = window;

  return (
    <div style={{ padding: 10, height: height - 124 }}>
      <Search style={{ marginBottom: 8 }} placeholder="Tìm kiếm" onChange={onChange} />
      <Tree
        onExpand={onExpand}
        expandedKeys={expandedKeys}
        autoExpandParent={autoExpandParent}
        treeData={filterData}
        height={height - 180}
        onSelect={onChooseArea}
        selectedKeys={[area.active]}
      />
    </div>
  );
}

export default memo(SiderLayout);
